import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import ThemeToggle from "./ThemeToggle";
import { useTheme } from "./ThemeProvider";

const LINKS = [
  { href: "#problems", label: "The problem", testid: "nav-problems" },
  { href: "#founders", label: "Who we are", testid: "nav-founders" },
  { href: "#contact", label: "Contact", testid: "nav-contact" },
];

function BrandLogo({ className = "" }) {
  const { theme } = useTheme();
  
  return (
    <div className="brand-logo">
      <img 
        src="/logo.png" 
        alt="SemiconOS" 
        className="brand-logo__image"
        style={{
          mixBlendMode: theme === 'light' ? 'lighten' : 'normal'
        }}
      />
    </div>
  );
}

export default function Nav() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      <nav data-testid="site-nav" className="fixed top-0 left-0 right-0 z-50 nav-shell">
        <div style={{ width: "100%", paddingRight: "2rem" }} className="pl-0 py-4 md:py-5 flex items-center justify-between">
          <a href="#top" data-testid="nav-logo" className="flex items-center gap-2 group" onClick={() => setOpen(false)}>
            <BrandLogo />
          </a>
          <div className="hidden md:flex items-center gap-10 text-lg" style={{ color: '#F1ECE3' }}>
            {LINKS.map((l) => (
              <a key={l.href} href={l.href} data-testid={l.testid} className="hover:opacity-80 transition-opacity">
                {l.label}
              </a>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle testId="theme-toggle-nav" />
            <a href="#contact" data-testid="nav-cta" className="hidden sm:inline-flex text-lg font-medium hover:opacity-80 transition-opacity" style={{ color: '#B69DE0' }}>
              Talk to us →
            </a>
            <button type="button" onClick={() => setOpen(true)} aria-label="Open menu" data-testid="nav-menu-button" className="md:hidden theme-toggle">
              <Menu size={18} strokeWidth={1.6} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile drawer */}
      <div
        data-testid="mobile-drawer"
        aria-hidden={!open}
        className={`fixed inset-0 z-[60] md:hidden transition-opacity duration-300 ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
      >
        <button aria-label="Close menu" onClick={() => setOpen(false)} data-testid="drawer-backdrop" className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
        <aside
          role="dialog"
          aria-modal="true"
          className={`absolute right-0 top-0 h-full w-[86%] max-w-sm border-l shadow-2xl flex flex-col transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`}
          style={{ background: "var(--bg)", borderColor: "var(--border-color)" }}
        >
          <div className="flex items-center justify-between px-6 py-5 border-b border-soft">
            <BrandLogo className="h-8 w-auto" />
            <button type="button" onClick={() => setOpen(false)} aria-label="Close menu" data-testid="drawer-close" className="theme-toggle">
              <X size={18} strokeWidth={1.6} />
            </button>
          </div>
          <nav className="flex-1 overflow-y-auto px-6 py-10 flex flex-col gap-6">
            {LINKS.map((l, i) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`drawer-${l.testid}`}
                onClick={() => setOpen(false)}
                className="font-display text-3xl text-strong hover:text-accent transition-colors leading-tight"
                style={{ transitionDelay: open ? `${i * 40}ms` : "0ms" }}
              >
                {l.label}
              </a>
            ))}
          </nav>
          <div className="px-6 py-6 pb-24 border-t border-soft flex items-center justify-between">
            <a href="#contact" data-testid="drawer-cta" onClick={() => setOpen(false)} className="btn-primary">
              Talk to us <span aria-hidden>→</span>
            </a>
            <ThemeToggle testId="theme-toggle-drawer" />
          </div>
        </aside>
      </div>
    </>
  );
}
